import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Avatar } from '@mui/material';

// Paleta de acentos consistente con el resto del módulo de préstamos.
export const ACCENTS = {
  teal: { accent: '#18586b', valueColor: '#18586b' },
  green: { accent: '#2e7d32', valueColor: '#1b5e20' },
  orange: { accent: '#b26a00', valueColor: '#8a5200' },
  purple: { accent: '#6a1b9a', valueColor: '#4a148c' },
  gray: { accent: '#9e9e9e', valueColor: 'text.secondary' },
};

// Formatea una fecha del backend (RFC 1123 "Mon, 26 May 2026 ..." o ISO
// "YYYY-MM-DD") a un formato legible: "26 may 2026". Para fechas ISO se
// construye con partes locales para evitar el desfase por zona horaria.
export function formatFecha(value) {
  if (!value) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const d = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Convierte una fecha del backend a 'YYYY-MM-DD' para inputs type="date".
export function toInputDate(value) {
  if (!value) return '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const ESTADO_CONFIG = {
  activo: { label: 'Activo', color: 'success' },
  inactivo: { label: 'Inactivo', color: 'default' },
  suspendido: { label: 'Suspendido', color: 'warning' },
  finalizado: { label: 'Finalizado', color: 'info' },
  cancelado: { label: 'Cancelado', color: 'error' },
};

// Título de sección con barra vertical de acento.
export function SectionTitle({ children, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 3, height: 18, borderRadius: 1, bgcolor: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '0.01em' }}>
          {children}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

// Tarjeta de métrica con barra de acento a la izquierda.
export function MetricCard({ label, value, sub, accent = '#18586b', valueColor = '#18586b' }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: accent },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          height: '100%',
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ py: 2, px: 2.5, pl: 3, '&:last-child': { pb: 2 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}
        >
          {label}
        </Typography>
        <Typography
          fontWeight={700}
          color={valueColor}
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, letterSpacing: '-0.01em', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Chip de estado con etiqueta capitalizada y color por estado.
export function EstadoChip({ estado, size = 'small' }) {
  const cfg = ESTADO_CONFIG[estado] || { label: estado || '—', color: 'default' };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size={size}
      sx={{ fontWeight: 600, height: 24 }}
    />
  );
}

// Avatar con iniciales (teal de marca).
export function InitialsAvatar({ name, size = 40 }) {
  const initials = (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 700,
        fontSize: size * 0.4,
      }}
    >
      {initials}
    </Avatar>
  );
}
